import { Button, Text } from '@react-spectrum/s2';
import ChevronRight from '@react-spectrum/s2/icons/ChevronRight';
import { useCallback, useState } from 'react';

import { TypewriterOnce } from '../components/TypewriterOnce';

type StrategyIntroScreenProps = {
	onContinue: () => void;
};

const BULLETS: Array<{ icon: string; label: string; body: string }> = [
	{
		icon: '/strategy-understanding-customers.svg',
		label: 'Understanding your customers',
		body: 'Who they are, what they care about, what keeps them up at night.',
	},
	{
		icon: '/strategy-researching-market.svg',
		label: 'Researching your market',
		body: "What people want, what's changing, where the gaps are.",
	},
	{
		icon: '/strategy-working-with-partners.svg',
		label: 'Working with partners',
		body: 'Other businesses who already reach more people than you do.',
	},
	{
		icon: '/strategy-adding-real-value.svg',
		label: 'Adding real value',
		body: 'Content and campaigns that mean something. Not just noise.',
	},
	{
		icon: '/strategy-staying-top-of-mind.svg',
		label: 'Staying top of mind',
		body: 'In the conversation, where decisions get made, before the moment they need you.',
	},
];

export const StrategyIntroScreen = ({
	onContinue,
}: StrategyIntroScreenProps) => {
	const [typingDone, setTypingDone] = useState(false);
	const handleTypingDone = useCallback(() => setTypingDone(true), []);

	return (
		<div className="flex h-full w-full items-center justify-center">
			<div className="w-full max-w-[760px] px-8">
				<h2
					className="mb-6"
					style={{
						fontSize: 'clamp(24px, 3.2vw, 36px)',
						fontWeight: 700,
						letterSpacing: '-0.02em',
						lineHeight: 1.15,
						color: 'var(--text-primary)',
					}}
				>
					<TypewriterOnce
						text="A good marketing strategy touches on a few things."
						onDone={handleTypingDone}
					/>
				</h2>

				<div
					style={{
						opacity: typingDone ? 1 : 0,
						transition: 'opacity 400ms ease',
						pointerEvents: typingDone ? 'auto' : 'none',
					}}
				>
					<ul className="mb-6 flex flex-col">
						{BULLETS.map((b, i) => (
							<li
								key={b.label}
								className="flex items-center gap-4 py-3"
								style={{
									borderTop:
										i === 0
											? 'none'
											: '1px solid color-mix(in srgb, var(--text-primary) 12%, transparent)',
								}}
							>
								<div
									className="flex items-center justify-center"
									style={{
										width: 40,
										height: 40,
										flexShrink: 0,
										background: '#FFFFFF',
										borderRadius: 14,
										boxShadow:
											'0 1px 2px color-mix(in srgb, var(--text-primary) 8%, transparent)',
									}}
								>
									<img
										src={b.icon}
										alt=""
										aria-hidden="true"
										style={{
											width: 24,
											height: 24,
											objectFit: 'contain',
										}}
									/>
								</div>
								<div className="flex flex-col">
									<div
										style={{
											fontSize: 15,
											fontWeight: 700,
											lineHeight: 1.3,
											color: 'var(--text-primary)',
										}}
									>
										{b.label}
									</div>
									<div
										style={{
											fontSize: 14,
											lineHeight: 1.4,
											color:
												'color-mix(in srgb, var(--text-primary) 65%, transparent)',
										}}
									>
										{b.body}
									</div>
								</div>
							</li>
						))}
					</ul>

					<Button
						variant="primary"
						staticColor="black"
						fillStyle="fill"
						size="L"
						onPress={onContinue}
					>
						<Text>Got it</Text>
						<ChevronRight />
					</Button>
				</div>
			</div>
		</div>
	);
};
